import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  APP_PERMISSION_META,
  type AppPermissionKey,
  type AppRoleDefinition,
} from "@domaddress/shared";

type MatrixPayload = {
  roles: AppRoleDefinition[];
  permissions: Record<string, Record<AppPermissionKey, boolean>>;
};

const SECTION_LABELS: Record<string, string> = {
  contacts: "Contacten",
  dominicusgangers: "Dominicusgangers",
  groups: "Groepen",
  beheer: "Beheer",
};

export default function AdminRolesMatrixPanel({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [matrix, setMatrix] = useState<MatrixPayload | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [newRoleKey, setNewRoleKey] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [roleMsg, setRoleMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<{ data: MatrixPayload }>("/api/admin/roles");
    setMatrix(res.data.data);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rowsBySection = useMemo(() => {
    const m = new Map<string, typeof APP_PERMISSION_META>();
    for (const p of APP_PERMISSION_META) {
      const list = m.get(p.section) ?? [];
      list.push(p);
      m.set(p.section, list);
    }
    return m;
  }, []);

  async function toggleCell(roleKey: string, perm: AppPermissionKey, enabled: boolean) {
    if (!isSuperAdmin) return;
    const cell = `${roleKey}:${perm}`;
    setBusyKey(cell);
    try {
      await api.patch(`/api/admin/roles/${encodeURIComponent(roleKey)}/permission`, {
        permission_key: perm,
        enabled,
      });
      await load();
    } finally {
      setBusyKey(null);
    }
  }

  async function addRole() {
    setRoleMsg(null);
    try {
      await api.post("/api/admin/roles", {
        key: newRoleKey.trim(),
        name: newRoleName.trim(),
        description: newRoleDesc.trim(),
      });
      setNewRoleKey("");
      setNewRoleName("");
      setNewRoleDesc("");
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Rol toevoegen mislukt";
      setRoleMsg(msg);
    }
  }

  async function removeRole(key: string) {
    if (!window.confirm(`Rol "${key}" verwijderen?`)) return;
    setRoleMsg(null);
    try {
      await api.delete(`/api/admin/roles/${encodeURIComponent(key)}`);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Verwijderen mislukt";
      setRoleMsg(msg);
    }
  }

  if (!matrix) {
    return <p className="data-list-row-muted">Rechtenmatrix laden…</p>;
  }

  const { roles, permissions } = matrix;

  return (
    <div className="detail-section" style={{ marginBottom: "1.5rem" }}>
      <h3 className="section-title">Rollen en rechtenmatrix</h3>
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        Kolommen zijn rollen; rijen zijn vaste rechten in de app. <strong>Superbeheerder</strong> heeft alles en staat niet in de matrix.
        {isSuperAdmin ? " Jij kunt vinkjes en rollen wijzigen." : " Alleen superbeheerder kan de matrix bewerken."}
      </p>

      {isSuperAdmin && (
        <div style={{ marginBottom: "1rem" }}>
          <h4 className="section-title" style={{ fontSize: "1rem" }}>Nieuwe rol</h4>
          <div className="add-row" style={{ flexWrap: "wrap", gap: 8 }}>
            <input
              className="filter-select"
              placeholder="sleutel (bijv. gast_lezer)"
              value={newRoleKey}
              onChange={(e) => setNewRoleKey(e.target.value)}
              style={{ minWidth: 160 }}
            />
            <input
              className="filter-select"
              placeholder="Naam in het Nederlands"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              style={{ minWidth: 180 }}
            />
            <input
              className="filter-select"
              placeholder="Korte omschrijving"
              value={newRoleDesc}
              onChange={(e) => setNewRoleDesc(e.target.value)}
              style={{ minWidth: 220, flex: 1 }}
            />
            <button type="button" className="btn-primary" onClick={() => void addRole()}>
              Rol toevoegen
            </button>
          </div>
          {roleMsg && <p style={{ color: "var(--danger, #b00020)" }}>{roleMsg}</p>}
        </div>
      )}

      <ul style={{ margin: "0 0 1rem", paddingLeft: "1.25rem" }}>
        {roles.map((r) => (
          <li key={r.key}>
            <strong>{r.name}</strong> ({r.key}) — {r.description}
            {isSuperAdmin && !r.is_builtin && (
              <button
                type="button"
                className="btn-secondary"
                style={{ marginLeft: 8, padding: "2px 8px", fontSize: "0.85em" }}
                onClick={() => void removeRole(r.key)}
              >
                Verwijderen
              </button>
            )}
          </li>
        ))}
      </ul>

      <div className="table-wrap--scroll">
        <table className="users-table users-table--compact" style={{ fontSize: "0.9em" }}>
          <thead>
            <tr>
              <th>Recht</th>
              <th title="Superbeheerder — altijd aan">Super</th>
              {roles.map((r) => (
                <th key={r.key} title={r.key}>
                  {r.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...rowsBySection.entries()].map(([section, perms]) => (
              <Fragment key={section}>
                <tr>
                  <td colSpan={2 + roles.length} style={{ fontWeight: 600, background: "var(--surface-2, #f4f4f4)" }}>
                    {SECTION_LABELS[section] ?? section}
                  </td>
                </tr>
                {perms.map((meta) => (
                  <tr key={meta.key}>
                    <td title={meta.hint ?? undefined}>
                      {meta.label}
                    </td>
                    <td style={{ textAlign: "center" }}>✓</td>
                    {roles.map((r) => {
                      const on = permissions[r.key]?.[meta.key] ?? false;
                      const cell = `${r.key}:${meta.key}`;
                      return (
                        <td key={r.key} style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={!isSuperAdmin || busyKey === cell}
                            onChange={(e) => void toggleCell(r.key, meta.key, e.target.checked)}
                            aria-label={`${meta.label} voor ${r.name}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
